/**
 * Truncates a URL for display purposes.
 * For URLs, it preserves the domain and first path segment but truncates the rest.
 * For non-URLs, it truncates to the specified length (default 50 chars).
 * 
 * @param {string} url - The URL or string to truncate
 * @param {number} maxLength - Maximum length for non-URL strings (default: 50)
 * @returns {Object} - Object with truncated text and tooltip properties
 */
export const truncateUrl = (url, maxLength = 50) => {
  if (!url) return { text: '—', tooltip: '—' };
  
  // If it's not a string, convert to string
  if (typeof url !== 'string') {
    url = String(url);
  }
  
  // Complete original as tooltip
  const tooltip = url;
  
  // Check if it looks like a URL (starts with http, https, or www)
  if (url.match(/^(https?:\/\/|www\.)/i)) {
    try {
      // For URLs that don't start with http/https, add https:// so URL parsing works
      const parsedUrl = new URL(url.startsWith('http') ? url : `https://${url}`);
      
      // Extract the domain and first path segment
      const domain = parsedUrl.hostname;
      const pathSegments = parsedUrl.pathname.split('/').filter(Boolean);
      const firstPathSegment = pathSegments.length > 0 ? `/${pathSegments[0]}` : '';
      
      // Check if we have query parameters
      const hasParams = parsedUrl.search.length > 0;
      
      // Create a readable truncated version
      const truncated = `${domain}${firstPathSegment}${hasParams ? '?...' : ''}`;
      
      return { text: truncated, tooltip };
    } catch (e) {
      // If URL parsing fails, fall back to simple truncation
      console.warn('URL parsing failed:', e);
    }
  }
  
  // Default truncation for non-URLs or if URL parsing failed
  if (url.length <= maxLength) {
    return { text: url, tooltip };
  }
  
  const truncated = `${url.substring(0, maxLength)}...`;
  return { text: truncated, tooltip };
};